import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/context/AuthContext'
import { login as apiLogin, getAuthProviders, googleLogin } from '@/api/auth'
import AuthShell, { AuthInput } from './AuthShell'
import { ArrowLeft } from 'lucide-react'
import { startTelegramOidcLogin } from '@/utils/telegramOidc'
import GoogleSignInButton from '@/components/GoogleSignInButton'

const OIDC_CLIENT_ID = import.meta.env.VITE_TELEGRAM_OIDC_CLIENT_ID || ''

export default function LoginPage() {
  const { t }     = useTranslation()
  const { login } = useAuth()
  const navigate  = useNavigate()

  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError]       = useState(null)
  const [loading, setLoading]   = useState(false)
  const [providers, setProviders] = useState(null)

  // Ask the backend which social logins are configured so we only show a
  // button when it will actually work.
  useEffect(() => {
    const controller = new AbortController()
    getAuthProviders({ signal: controller.signal })
      .then(({ data }) => setProviders(data))
      .catch(() => { /* leave social buttons hidden on failure */ })
    return () => controller.abort()
  }, [])

  // Surface an error stashed by the Telegram OIDC callback gate (App.jsx).
  // Done in an effect, not a useState initializer, so React StrictMode's
  // double-invoke in dev doesn't consume the sessionStorage flag before the
  // "real" render reads it.
  useEffect(() => {
    const oidcError = sessionStorage.getItem('tg_oidc_error')
    if (oidcError) {
      sessionStorage.removeItem('tg_oidc_error')
      setError(oidcError)
    }
  }, [])

  function handleTelegramOidcLogin() {
    startTelegramOidcLogin(OIDC_CLIENT_ID, `${window.location.origin}/`)
  }

  async function handleGoogleCredential(idToken) {
    setLoading(true); setError(null)
    try {
      const { data } = await googleLogin(idToken)
      login({ access: data.access, refresh: data.refresh, user: data.user })
      navigate('/', { replace: true })
    } catch (err) {
      setError(err.response?.data?.detail ?? t('auth.loginFailed', 'Login muvaffaqiyatsiz'))
    } finally {
      setLoading(false)
    }
  }

  async function handleSubmit(e) {
    e.preventDefault()
    if (!username || !password) return
    setLoading(true); setError(null)
    try {
      const { data } = await apiLogin(username, password)
      login({ access: data.access, refresh: data.refresh, user: data.user })
      navigate('/', { replace: true })
    } catch (err) {
      setError(err.response?.data?.detail ?? t('auth.loginFailed', 'Login muvaffaqiyatsiz'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <AuthShell>
      <h2 className="fw-7 t-xl" style={{ color: 'var(--ink)', marginBottom: 6 }}>
        {t('auth.loginTitle', 'Hisobingizga kiring')}
      </h2>
      <p className="t-sm t-mut" style={{ marginBottom: 24 }}>
        {t('auth.noAccount', "Hisob yo'q?")}{' '}
        <Link to="/register" style={{ color: 'var(--coral)', fontWeight: 600, textDecoration: 'none' }}>
          {t('auth.register', "Ro'yxatdan o'tish")}
        </Link>
      </p>

      {error && (
        <div className="t-sm" style={{
          marginBottom: 16, padding: '12px 16px', borderRadius: 12,
          background: 'var(--danger-tint)', border: '1px solid var(--danger)', color: 'var(--danger)',
        }}>
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <AuthInput
          label={t('auth.username', 'Foydalanuvchi nomi')}
          value={username} onChange={(e) => setUsername(e.target.value)}
          autoComplete="username" required
        />
        <AuthInput
          label={t('auth.password', 'Parol')}
          type="password" value={password} onChange={(e) => setPassword(e.target.value)}
          autoComplete="current-password" required
        />
        <button
          type="submit" disabled={loading}
          className="btn btn-primary" style={{ width: '100%', marginTop: 4, justifyContent: 'center' }}
        >
          {loading ? t('common.loading', 'Yuklanmoqda…') : t('auth.login', 'Kirish')}
        </button>
      </form>

      {(OIDC_CLIENT_ID || providers?.google?.enabled) && (
        <>
          <div className="row gap-8 t-xs t-mut" style={{ margin: '20px 0', alignItems: 'center' }}>
            <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
            <span>yoki</span>
            <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {providers?.google?.enabled && (
              <GoogleSignInButton
                clientId={providers.google.client_id}
                onCredential={handleGoogleCredential}
                onError={() => setError(t('auth.googleLoadFailed', "Google login'ni yuklab bo'lmadi"))}
              />
            )}
            {OIDC_CLIENT_ID && (
              <button
                type="button"
                onClick={handleTelegramOidcLogin}
                disabled={loading}
                className="btn"
                style={{ width: '100%', justifyContent: 'center', background: '#2ba5e0', color: '#fff', display: 'flex', alignItems: 'center', gap: 8 }}
              >
                Telegram orqali kirish
              </button>
            )}
          </div>
        </>
      )}

      <div style={{ marginTop: 20, textAlign: 'center' }}>
        <Link
          to="/"
          className="row gap-6 t-xs t-mut"
          style={{ justifyContent: 'center', textDecoration: 'none', color: 'var(--ink-3)' }}
        >
          <ArrowLeft size={13} strokeWidth={2} />
          {t('auth.backToExams', 'Imtihonlarga qaytish')}
        </Link>
      </div>
    </AuthShell>
  )
}
